import { ARENA, FOCUS, PLAYER } from '../../config/constants.js';

// Combat surge: the generic power spike that rides along with EVERY
// champion ultimate (systems/combat/UltimateSystem.js), on top of whatever
// the ultimate's own def does. Previously this was the standalone Awakening
// transformation on its own Focus meter and its own button; folding it into
// the ultimate leaves one meter and one button (Space) for "the big moment".
//
// Its window is a FIXED length rather than the ultimate's own duration.
// Durations run 2.2s (Requiem) to 8s (Bastion), so riding along with them
// handed Rook 3.6x the surge value it handed Vess — and handed the most
// offensive uptime to the champion whose ultimate is the defensive one.
// A fixed window makes the surge worth exactly the same to everyone and
// leaves per-champion balance where it belongs: in the defs themselves.
const SURGE_DURATION = 4; // seconds
// Trimmed from the standalone Awakening's 1.5/1.5/0.2 because the surge is
// no longer something a run pays a 30-Shard perk slot for — it now comes
// free with five already-strong ultimates, AND it multiplies each ultimate's
// own damage (CombatSystem._getDamageMultiplier feeds projectiles, area
// damage and dash AoE alike), so the defs' tuned numbers ride it too.
const SURGE_DAMAGE_MULTIPLIER = 1.3;
const SURGE_FIRE_RATE_MULTIPLIER = 1.4; // see CombatSystem.update's effective-dt trick
const SURGE_DODGE_BONUS = 0.15;

// Level-up passive tuning (see data/levelUpPool.js). Momentum/Adrenaline
// layer on top of moveSpeedMultiplier via effectiveMoveSpeedMultiplier
// below rather than mutating it directly, the same way the ultimate surge
// layers on top of damageMultiplier instead of merging into it.
const MOMENTUM_CAP = 0.25;
const ADRENALINE_SPEED_BONUS = 0.30;
const ADRENALINE_DAMAGE_MULTIPLIER = 1.2;
const ADRENALINE_HP_THRESHOLD = 0.5;

// Bulletdancer (level-up card): how long a single graze's damage stack
// lasts. Long enough that a sustained bullet pattern holds the stack up,
// short enough that it drops during a lull instead of being permanent.
const GRAZE_STACK_DURATION = 4; // seconds
// Pristine Edge (level-up card): the HP fraction at or above which its
// damage bonus applies. Deliberately not 100% — chip damage shouldn't
// switch the card off for the rest of the run.
const PRISTINE_HP_THRESHOLD = 0.8;

// Slipstream (Maelstrom/wind legendary pet, see data/pets.js): a short window
// after every dash granting bonus move speed and dash range (see the post-dash
// getters below). Length of that window:
const POST_DASH_DURATION = 2; // seconds

// Wellspring (water legendary pet): a clutch heal that fires when a
// hit drops the player below half HP, then goes on this cooldown so it's a
// lifeline rather than steady sustain.
const WELLSPRING_COOLDOWN = 6; // seconds

// Vampiric/Bloodmoon heals per hit landed, which can be several times a
// second — showing a "player:healed" pop-up for every tick would spam the
// screen with "+1"s. Instead the healed amounts are banked in
// _lifestealAccum and flushed as one combined event on this cadence, same
// idea as a UI throttle.
const LIFESTEAL_FLUSH_INTERVAL = 0.35; // seconds

// Act III — the movement-history ring buffer (ACT3-BIBLE §9's first engine
// touch). The act's shared texture is "the arena is made out of the player",
// and the cheapest, most reusable piece of that is simply remembering where the
// player has been: the Ostinato replays it as hazard, the Tracer minion walks
// it. Sampled on a fixed cadence rather than every tick (60 Hz of positions is
// far finer than anything reads) and trimmed to a bounded window, so the buffer
// is a few dozen entries and costs nothing in the fights that never look at it.
const PATH_SAMPLE_INTERVAL = 1 / 20; // seconds between recorded positions
const PATH_HISTORY_SECONDS = 5; // how far back the buffer reaches

// Brief red flinch tint on taking a real hit — same hitFlashTimer pattern
// Boss.js and every minion class use (the player was the one entity
// without it). Red, not white: white is already the dash-i-frame tell.
const HIT_FLASH_DURATION = 0.1;

/**
 * Player character: WASD movement + Shift dash. Dash grants a short burst
 * of speed with i-frames covering the burst, then a per-charge cooldown
 * before that charge is usable again (1 charge by default, 2 with the Twin
 * Dash perk — see maxDashCharges). This is the player's primary defensive
 * tool against the boss's bullet-hell patterns; Phase Step (dodgeChance),
 * the ultimate surge, and Rook's Aegis plates are the only passive
 * mitigation.
 *
 * `signature` is the chosen champion's always-on mechanic (see
 * data/characters.js). This class owns the halves of it that are about the
 * player's own state — Cadence's hit streak, Aegis plates, and the crit
 * chance Killing Edge scales off missing HP. The halves that resolve at hit
 * time (crit rolls, Briar Aura, Slipstream's dash chain) live in
 * CombatSystem, which is where damage is actually applied.
 */
export class Player {
  constructor(eventBus, {
    x = ARENA.x + ARENA.width / 2,
    y = ARENA.y + ARENA.height / 2,
    maxHpBonus = 0,
    maxHpMultiplier = 1,
    moveSpeedMultiplier = 1,
    dashCooldownReduction = 0,
    dashCooldownPct = 0,
    dashRangeMultiplier = 1,
    maxDashCharges = 1,
    dodgeChance = 0,
    hpRegenPerSecond = 0,
    hpRegenPercent = 0,
    lifestealPercent = 0,
    fireRateMultiplier = 1,
    damageReduction = 0,
    minionDamageMultiplier = 1,
    bossDamageMultiplier = 1,
    ultimateChargeGainMultiplier = 1,
    petCritChance = 0,
    petCritDamageBonus = 0,
    executeThreshold = 0,
    wellspringHealPct = 0,
    postDashMoveBonus = 0,
    postDashDashRangeBonus = 0,
    signature = null,
  } = {}) {
    this.eventBus = eventBus;
    this.x = x;
    this.y = y;
    this.radius = PLAYER.radius;
    this.maxHp = (PLAYER.maxHp + maxHpBonus) * maxHpMultiplier;
    this.hp = this.maxHp;
    this.hpRegenPerSecond = hpRegenPerSecond; // flat HP/sec (meta shop's Recovery)
    this.hpRegenPercent = hpRegenPercent; // water pets: fraction of max HP per second

    // Set from permanent meta upgrades (M4 SaveManager) combined with the
    // chosen character's passive (M5), and further adjustable in-run by
    // level-up passive picks (moveSpeedMultiplier).
    this.moveSpeedMultiplier = moveSpeedMultiplier;
    // Frost pets/Permafrost cut a PERCENTAGE of the base cooldown; the meta
    // shop's Reflex Core then subtracts its flat seconds on top of that.
    this.dashCooldown = Math.max(0.5, PLAYER.dashCooldown * (1 - dashCooldownPct) - dashCooldownReduction);
    this.dashRangeMultiplier = dashRangeMultiplier;
    this.dodgeChance = dodgeChance;

    // Set only by in-run level-up passive picks (see data/levelUpPool.js) —
    // all default to a no-op value so nothing changes until a passive is
    // actually picked.
    this.lifestealPercent = lifestealPercent; // Vampiric / Bloodmoon
    this.minionDamageMultiplier = minionDamageMultiplier; // Swarmbane pet base × Executioner passive — consumed in CombatSystem
    this.bossDamageMultiplier = bossDamageMultiplier; // Giant Slayer pet (Mythic Slayer) — consumed in CombatSystem
    this.momentumRampPerSec = 0; // Momentum
    this._timeSinceDamage = 0; // Momentum's ramp clock
    this.hasAdrenaline = false; // Adrenaline
    this.fireRateMultiplier = fireRateMultiplier; // Rapid Fire / Overclock — consumed in CombatSystem
    this.damageReduction = damageReduction; // Stoneskin pet base × Bulwark passive — consumed in takeDamage()
    this.incomingDamageMultiplier = 1;

    // Pact costs/boons (see data/levelUpPool.js PACT_POOL) — all default to
    // a no-op value, same convention as the passive fields above.
    this.podSpawnDisabled = false; // Hunger — consumed by PickupSystem's spawn gate
    this.bossPickSpeedMultiplier = 1; // Gilded Pact — consumed by SpawningSystem._startNextPattern
    this.echoPactMultiplier = 1; // Gilded Pact — folded into both echo payouts in Game.js
    this.ultimateChargeGainMultiplier = ultimateChargeGainMultiplier; // Overdrive pet base × Crescendo pact — consumed in UltimateSystem.addCharge
    this.ultimateDurationMultiplier = 1; // Crescendo — consumed in UltimateSystem.activate
    this.ultimateDrainOnHit = 0; // Reckless Pact — consumed in UltimateSystem's player:damaged listener

    // --- level-up cards with behavior of their own (data/levelUpPool.js) ---
    // Each defaults to its no-op value, same convention as the fields above.
    // Bulletdancer: grazing a bullet banks a short damage stack, so hugging
    // fire pays out in damage and not just ultimate charge.
    this.grazeDamagePerStack = 0;
    this.grazeMaxStacks = 0;
    this.grazeStacks = 0;
    this._grazeTimer = 0;
    // Pristine Edge: the mirror of Adrenaline — rewards an untouched HP bar
    // instead of a nearly-empty one, so the two pull a build in opposite
    // directions rather than stacking into the same "stay alive" advice.
    this.pristineDamageBonus = 0;
    // Opening Gambit: bonus damage against enemies still at full HP —
    // consumed per-target in CombatSystem, not through _getDamageMultiplier.
    this.fullHpTargetDamageBonus = 0;
    // Cascade: minion deaths detonate. Wired in Game.js off minion:died,
    // which already carries the position.
    this.killBlastDamage = 0;
    // Reprisal: a weaker Retribution — wired in Game.js off player:damaged.
    this.reprisalDamage = 0;
    // Spoils of War: clearing a wave stage heals. Wired in Game.js.
    this.waveClearHealPercent = 0;
    // Field Medic: read by PickupSystem when a pod is collected.
    this.podHealBonus = 0;
    // Hairtrigger: card crit, folded into the crit getters alongside the
    // pet's (Keen Edge) and Iris's signature.
    this.cardCritChance = 0;
    this.cardCritDamageBonus = 0;

    eventBus.on('bullet:grazed', () => {
      if (this.grazeMaxStacks === 0) return;
      this.grazeStacks = Math.min(this.grazeMaxStacks, this.grazeStacks + 1);
      this._grazeTimer = GRAZE_STACK_DURATION;
    });

    // Pet uniques (data/pets.js), fixed for the whole run.
    this.petCritChance = petCritChance; // Mythic Edge (Keen Edge) — folded into the crit getters below
    this.petCritDamageBonus = petCritDamageBonus;
    this.executeThreshold = executeThreshold; // Rainbow Reaper (Judgment) — consumed in CombatSystem
    this.wellspringHealPct = wellspringHealPct; // Wellspring (water legendary) — clutch heal (% max HP) in takeDamage()
    this._wellspringCooldown = 0;
    // Slipstream (wind legendary pet): a post-dash window granting bonus
    // move speed + dash range. `_postDashTimer` is that window's clock, set on
    // dash and read by the post-dash getters below.
    this.postDashMoveBonus = postDashMoveBonus;
    this.postDashDashRangeBonus = postDashDashRangeBonus;
    this._postDashTimer = 0;

    // --- champion signature (data/characters.js), fixed for the whole run ---
    // Vess's Cadence: a hit streak that ramps fire rate. `_cadenceGrace` is
    // the lull timer — stacks hold while it runs and bleed off once it
    // lapses, so a fight with any rhythm at all sustains them while a long
    // repositioning gap doesn't.
    this.cadenceConfig = signature?.cadence ?? null;
    this.cadenceStacks = 0;
    this._cadenceGrace = 0;
    this._cadenceDecayAccum = 0;
    // Rook's Aegis Plating: each plate eats one incoming hit whole (see
    // takeDamage). Starts full — the first fight shouldn't be the one Rook
    // spends waiting to become Rook.
    this.maxArmorPlates = signature?.armorPlates?.max ?? 0;
    this.armorPlates = this.maxArmorPlates;
    this._plateRechargeSeconds = signature?.armorPlates?.rechargeSeconds ?? 0;
    this._plateTimer = this._plateRechargeSeconds;
    // Iris's Killing Edge — read at hit time by CombatSystem through the
    // critChance/critMultiplier getters below.
    this.critConfig = signature?.crit ?? null;
    // Aria's Refrain (Act II) — banked and released entirely inside
    // CombatSystem, which needs the config at hit time rather than here.
    this.refrainConfig = signature?.refrain ?? null;
    // Cairn's Debt (Act II): damage taken is banked rather than only spent,
    // and repaid as healing once Cairn goes untouched long enough to earn it.
    this.debtConfig = signature?.debt ?? null;
    this.debtBank = 0;
    this._debtHealAccum = 0;
    // Quill's Inscription (Act III): standing still writes a glyph at your feet
    // that outlives you standing there. The verb nobody else has is
    // field-building — every other champion's power moves with them. The glyphs
    // themselves are kept here (they're player state, and the HUD and the
    // renderer both read them); their damage tick is CombatSystem's, which is
    // where every damage source in this game resolves.
    this.inscriptionConfig = signature?.inscription ?? null;
    this.glyphs = [];
    this._writeTimer = signature?.inscription?.writeTime ?? 0;
    // Fair Copy (Quill's ultimate): glyphs written instantly and never expiring.
    this.inscriptionInstant = false;
    // Fugue's Countersubject (Act III): two stances, swapped on a cooldown.
    // Mastery is swap timing rather than stance choice, which is why the stance
    // you LEFT keeps paying out for `swapEchoSeconds` after a swap.
    this.stanceConfig = signature?.stances ?? null;
    this.stance = this.stanceConfig ? 'statement' : null;
    this._stanceSwapTimer = 0;
    this._stanceEcho = 0;
    this._stanceEchoFrom = null;
    // Stretto (Fugue's ultimate): free swaps, and both stances at once.
    this.bothStances = false;

    // --- ultimate-driven flags (data/ultimates.js) ---
    // All neutral outside an active ultimate; UltimateSystem guarantees the
    // owning def's onEnd runs, so none of these can be left switched on.
    this.enemyTimeScale = 1; // Zero Hour — consumed by Game._update
    this.freeDash = false; // Skybreaker — dashes cost no charges below
    this.ultimateDamageReduction = 0; // Bastion — consumed in takeDamage()

    this.facing = { x: 0, y: -1 };

    // Act III's movement history — oldest first, each `{ x, y, age }` where
    // `age` is seconds since it was recorded (advanced every update). Read
    // through positionAgo/pathBetween below; nothing writes to it but
    // _recordPath. Empty and inert until the player has actually moved.
    this.pathHistory = [];
    this._pathSampleTimer = 0;

    // Charge-based dash: each charge recharges independently on its own
    // timer (in `_rechargeTimers`) rather than one shared cooldown gate —
    // `_rechargeTimers[0]` is always the soonest to finish since entries
    // are pushed in trigger order and all count down at the same rate.
    this.maxDashCharges = maxDashCharges;
    this._dashCharges = maxDashCharges;
    this._rechargeTimers = [];
    this._isDashingBurst = false;
    this._dashTimer = 0; // counts down within the current burst
    this._dashDir = { x: 0, y: 0 };

    // Transient recoil velocity (px/sec) applied on top of movement and decayed
    // each tick — used for the dash-striker's bounce-back off a landed dash
    // (see CombatSystem._applyDashAoe / applyKnockback).
    this._knockback = { x: 0, y: 0 };

    this.isInvincible = false;
    // Act II — The Antiphon (polarity). The player carries an attunement, a
    // tone flipped by every dash. Bullets tagged with a matching tone pass
    // through harmlessly; the opposite tone bites harder (see BossBullet.tone
    // and SpawningSystem._checkPlayerCollisions). Always maintained but inert
    // in every fight but the Antiphon's, where bullets actually carry tones.
    this.attunement = 'warm';
    // Tine's Attunement (BULLET-HELL.md §D7's champion promotion). Her signature
    // tones every bullet in every fight (systems/combat/attunement.js), which
    // makes the tone flip the input the run is played on — so she gets it as a
    // key of its own (KEYBINDS.stanceSwap, shared with Fugue: only one champion's
    // verb is ever live) instead of only as a rider on the dash. The dash still
    // flips, and deliberately: a dash through a wall is a flip you paid for.
    this.attuneConfig = signature?.attune ?? null;
    this._attuneFlipTimer = 0;
    // Unison (Tine's ultimate): every bullet matches, whatever its tone —
    // read by SpawningSystem._checkPlayerCollisions.
    this.unison = false;
    // Act II — The Great Hush. While standing in a hush zone the player's
    // weapons and aura go dumb: set true per-frame by the Hushfall pattern,
    // read by CombatSystem.update to suppress fire. Inert (stays false) in
    // every other fight.
    this.muted = false;
    // Post-hit mercy window (PLAYER.hitInvulnDuration), deliberately SEPARATE
    // from isInvincible: that flag is the dash's proactive i-frame and also
    // gates graze credit (SpawningSystem._checkPlayerCollisions) — taking a
    // hit must never pay out ultimate charge the way a dash-through does.
    // Without this window every damage source resolves independently in the
    // same tick, so being surrounded by a wave (each minion carrying its own
    // 0.5s contact cooldown) deleted a full HP bar with no counterplay.
    this._hitInvulnTimer = 0;
    this.hitFlashTimer = 0; // see Game.js's _drawPlayer paletteOverride

    // Focus mode (BULLET-HELL.md §D4). `isFocused` is the raw held state;
    // `focusReveal` is its eased 0..1 render weight, so the hull dot and
    // graze ring fade rather than pop (Game._drawFocusHitbox).
    this.isFocused = false;
    this.focusReveal = 0;

    // Deathbomb (BULLET-HELL.md §D3 / DEATHBOMB in config/constants.js).
    // Seconds a landed hit hangs unresolved before it comes off HP; 0
    // disables the mechanic outright (the settings toggle). `canDeathbomb`
    // is the predicate for "is there anything to spend on it" — Game wires
    // it to UltimateSystem.isReady, since the Player must not know what an
    // ultimate is. Both are set from outside; the defaults here are the
    // no-deathbomb behaviour every existing test and tool already expects.
    this.deathbombWindow = 0;
    this.canDeathbomb = null;
    // { amount, source, remaining } while a hit is in the air, else null.
    this._pendingHit = null;

    // Runs on its own SURGE_DURATION clock, deliberately NOT the ultimate's
    // — so it can outlast a short ultimate (Requiem) and fall short of a long
    // one (Bastion). Crescendo's ultimateDurationMultiplier shortens the
    // ultimate window only; the surge is the same length for every build.
    this._surgeTimer = 0;
    eventBus.on('ultimate:activated', () => { this._surgeTimer = SURGE_DURATION; });

    // Cheap no-ops while lifestealPercent is 0 / momentumRampPerSec is 0, so
    // there's no need to gate registration behind whether the relevant
    // passive was actually picked.
    // Boss and minion damage both count (mirrors UltimateSystem's own charge
    // listeners). Desperation mode needs no extra gating here: Boss.takeDamage
    // itself no-ops while invulnerable, so 'boss:damaged' simply never fires
    // during that window, and minions don't exist during the boss stage
    // (cleared before it starts — see Game.js's _startBossStage), so neither
    // source can heal the player through the desperation finale.
    this._lifestealAccum = 0; // banked healed amount awaiting its throttled player:healed flush
    this._lifestealFlushTimer = LIFESTEAL_FLUSH_INTERVAL;
    const applyLifesteal = ({ amount }) => {
      if (this.lifestealPercent > 0 && this.hp > 0) {
        const hpBefore = this.hp;
        this.hp = Math.min(this.maxHp, this.hp + amount * this.lifestealPercent);
        this._lifestealAccum += this.hp - hpBefore;
      }
    };
    eventBus.on('boss:damaged', applyLifesteal);
    eventBus.on('minion:damaged', applyLifesteal);
    eventBus.on('player:damaged', () => {
      this._timeSinceDamage = 0;
      // Cadence is a streak, not a stockpile: a landed hit ends it outright
      // rather than trimming it, which is what makes it a genuine "don't get
      // touched" incentive instead of a passive that ticks up regardless.
      if (this.cadenceStacks > 0) {
        this.cadenceStacks = 0;
        this.eventBus.emit('player:cadence-broken');
      }
    });

    // Same unconditional registration as the lifesteal listeners above — a
    // no-op while cadenceConfig is null (every champion but Vess).
    const addCadence = () => this._addCadence();
    eventBus.on('boss:damaged', addCadence);
    eventBus.on('minion:damaged', addCadence);
  }

  // --- Cairn's Debt (data/characters.js) ------------------------------------

  /** 0..1 how much of a full HP bar is currently banked — drives the HUD tell. */
  get debtFraction() {
    return this.debtConfig ? Math.min(1, this.debtBank / this.maxHp) : 0;
  }

  /**
   * Repays banked damage as healing, but only once Cairn has gone
   * `repayAfter` seconds untouched — `_timeSinceDamage` is reset by the
   * player:damaged listener, so any hit restarts the wait. Taking a hit and
   * then playing clean is what earns it back.
   */
  _updateDebt(dt) {
    if (!this.debtConfig || this.debtBank <= 0 || this.hp <= 0) return;
    if (this._timeSinceDamage < this.debtConfig.repayAfter) return;
    const repaid = Math.min(this.debtBank, this.debtConfig.repayRate * dt, this.maxHp - this.hp);
    if (repaid <= 0) {
      // Already at full HP: the bank is spent rather than held forever, so a
      // topped-off Cairn can't stockpile a fight's worth of debt for later.
      this.debtBank = Math.max(0, this.debtBank - this.debtConfig.repayRate * dt);
      return;
    }
    this.hp += repaid;
    this.debtBank -= repaid;
    this._debtHealAccum += repaid;
  }

  // --- Quill's Inscription (Act III) ----------------------------------------

  /** True while standing on any live glyph — the buff condition, and the HUD tell. */
  get onGlyph() {
    if (!this.inscriptionConfig) return false;
    return this.glyphs.some((g) => Math.hypot(this.x - g.x, this.y - g.y) < g.radius);
  }

  /** Inscription's standing-still fire bonus — folded into CombatSystem's fire-rate chain. */
  get inscriptionFireRateMultiplier() {
    if (!this.inscriptionConfig || !this.onGlyph) return 1;
    return 1 + this.inscriptionConfig.standingFireBonus;
  }

  /**
   * A stand-still champion in a bullet-hell is a contradiction by design, and
   * `writeTime` is the knob that decides whether that's tension or misery: long
   * enough that a glyph is a real commitment, short enough that a lull in the
   * pattern is enough to place one. Any movement or dash resets the count —
   * partial credit would make it a passive rather than a decision.
   */
  _updateInscription(dt, moveVec) {
    if (!this.inscriptionConfig) return;
    const cfg = this.inscriptionConfig;
    for (let i = this.glyphs.length - 1; i >= 0; i--) {
      // Fair Copy freezes the whole field in place for its window; a glyph that
      // aged out mid-ultimate would punch a hole in the lattice it exists to make.
      if (!this.inscriptionInstant) this.glyphs[i].life -= dt;
      if (this.glyphs[i].life <= 0) this.glyphs.splice(i, 1);
    }
    const still = moveVec.x === 0 && moveVec.y === 0 && !this.isDashing;
    if (!still) {
      this._writeTimer = cfg.writeTime;
      return;
    }
    this._writeTimer -= this.inscriptionInstant ? cfg.writeTime : dt;
    if (this._writeTimer > 0) return;
    this._writeTimer = cfg.writeTime;
    // Newest overwrites oldest, so the field is a shape the player maintains
    // rather than one that accumulates until the arena is paved.
    if (this.glyphs.length >= cfg.maxGlyphs) this.glyphs.shift();
    this.glyphs.push({ x: this.x, y: this.y, life: cfg.glyphLifetime, radius: cfg.glyphRadius });
    this.eventBus.emit('player:glyph-written', { x: this.x, y: this.y });
  }

  // --- Fugue's Countersubject (Act III) -------------------------------------

  /** True while the stance swap is off cooldown (or free, under Stretto). */
  get canSwapStance() {
    return !!this.stanceConfig && (this.bothStances || this._stanceSwapTimer <= 0);
  }

  /** 0..1 fill of the swap cooldown — drives its HUD tell. */
  get stanceSwapFraction() {
    if (!this.stanceConfig) return 0;
    if (this.bothStances || this._stanceSwapTimer <= 0) return 1;
    return 1 - this._stanceSwapTimer / this.stanceConfig.swapCooldown;
  }

  /** True if `name`'s bonuses apply right now — the live stance, its fading echo, or both under Stretto. */
  inStance(name) {
    if (!this.stanceConfig) return false;
    if (this.bothStances) return true;
    if (this.stance === name) return true;
    return this._stanceEcho > 0 && this._stanceEchoFrom === name;
  }

  /** True while Tine's tone flip is off cooldown (and false for everyone else). */
  get canFlipAttunement() {
    return !!this.attuneConfig && this._attuneFlipTimer <= 0;
  }

  /** 0..1 readiness of the flip — drives the HUD ring's cooldown wedge. */
  get attuneFlipFraction() {
    if (!this.attuneConfig) return 0;
    if (this._attuneFlipTimer <= 0) return 1;
    return 1 - this._attuneFlipTimer / this.attuneConfig.flipCooldown;
  }

  /**
   * Tine's tone flip (KEYBINDS.stanceSwap). Short cooldown rather than free:
   * a free flip makes the correct play "hold the key down and never be hit by
   * anything", which deletes the read the signature exists to ask for. Long
   * enough to be a decision, short enough that a misread is recoverable one
   * bullet later. Returns false (and does nothing) for every other champion.
   */
  flipAttunement() {
    if (!this.canFlipAttunement) return false;
    this.attunement = this.attunement === 'warm' ? 'cold' : 'warm';
    this._attuneFlipTimer = this.attuneConfig.flipCooldown;
    this.eventBus.emit('player:attunement-flipped', { attunement: this.attunement });
    return true;
  }

  swapStance() {
    if (!this.canSwapStance) return false;
    // The echo is what makes swap TIMING the skill: for a moment after a swap
    // you have both, so a well-placed swap is worth more than picking right.
    this._stanceEchoFrom = this.stance;
    this._stanceEcho = this.stanceConfig.swapEchoSeconds;
    this.stance = this.stance === 'statement' ? 'answer' : 'statement';
    if (!this.bothStances) this._stanceSwapTimer = this.stanceConfig.swapCooldown;
    this.eventBus.emit('player:stance-swapped', { stance: this.stance });
    return true;
  }

  /** Statement's fire-rate edge — folded into CombatSystem's fire-rate chain. */
  get stanceFireRateMultiplier() {
    if (!this.stanceConfig || !this.inStance('statement')) return 1;
    return 1 + this.stanceConfig.statementFireBonus;
  }

  /** Answer's mitigation, and Statement's matching exposure — both consumed in takeDamage. */
  get stanceDamageReduction() {
    if (!this.stanceConfig) return 0;
    let reduction = 0;
    if (this.inStance('answer')) reduction += this.stanceConfig.answerDamageReduction;
    // Statement trades safety for output. Under Stretto (bothStances) the
    // penalty is waived along with everything else, which is the point of it.
    if (!this.bothStances && this.inStance('statement') && this.stance === 'statement') {
      reduction -= this.stanceConfig.statementExposure;
    }
    return reduction;
  }

  _updateStance(dt) {
    if (!this.stanceConfig) return;
    if (this._stanceSwapTimer > 0) this._stanceSwapTimer -= dt;
    if (this._stanceEcho > 0) {
      this._stanceEcho -= dt;
      if (this._stanceEcho <= 0) this._stanceEchoFrom = null;
    }
  }

  /** Vess's Cadence: fire-rate bonus from the current hit streak — consulted by CombatSystem alongside the surge/tailwind multipliers. */
  get cadenceFireRateMultiplier() {
    if (!this.cadenceConfig) return 1;
    return 1 + this.cadenceStacks * this.cadenceConfig.perStack;
  }

  /** 0..1 fill of the Cadence streak — drives its HUD tell. */
  get cadenceFraction() {
    if (!this.cadenceConfig) return 0;
    return this.cadenceStacks / this.cadenceConfig.maxStacks;
  }

  /** Mythic Edge's flat pet crit, Hairtrigger's card crit, plus Iris's Killing Edge (missing-HP ramp) — all stack additively. */
  get critChance() {
    const base = this.petCritChance + this.cardCritChance;
    if (!this.critConfig) return base;
    const missing = 1 - Math.max(0, this.hp) / this.maxHp;
    return base + this.critConfig.chance + (this.critConfig.desperateChance - this.critConfig.chance) * missing;
  }

  get critMultiplier() {
    // A landed crit applies the single highest source rather than their
    // product — Iris's configured multiplier, the pet's, or the card's.
    const petMult = this.petCritChance > 0 ? 1 + this.petCritDamageBonus : 1;
    const cardMult = this.cardCritChance > 0 ? 1 + this.cardCritDamageBonus : 1;
    return Math.max(this.critConfig?.multiplier ?? 1, petMult, cardMult);
  }

  /** Bulletdancer's banked graze stacks — consulted by CombatSystem alongside the surge/adrenaline multipliers. */
  get grazeDamageMultiplier() {
    return 1 + this.grazeStacks * this.grazeDamagePerStack;
  }

  /** Pristine Edge: the damage bonus for an (almost) untouched HP bar. */
  get pristineDamageMultiplier() {
    if (this.pristineDamageBonus <= 0) return 1;
    return this.hp >= this.maxHp * PRISTINE_HP_THRESHOLD ? 1 + this.pristineDamageBonus : 1;
  }

  /** Rook's Bastion ultimate: every plate back at once, and the rebuild clock restarted. */
  restoreArmorPlates() {
    if (this.maxArmorPlates === 0) return;
    this.armorPlates = this.maxArmorPlates;
    this._plateTimer = this._plateRechargeSeconds;
    this.eventBus.emit('player:plates-restored', { plates: this.armorPlates });
  }

  /** Kestrel's Slipstream: a landed dash strike hands the charge straight back. */
  refundDashCharge() {
    if (this._dashCharges >= this.maxDashCharges) return;
    this._dashCharges++;
    // Drop the soonest-finishing recharge timer along with it, or the refund
    // would hand back a charge AND still complete the one already in flight.
    this._rechargeTimers.shift();
    this.eventBus.emit('player:dash-refilled', { charges: this._dashCharges });
  }

  _addCadence() {
    if (!this.cadenceConfig) return;
    this._cadenceGrace = this.cadenceConfig.graceSeconds;
    if (this.cadenceStacks >= this.cadenceConfig.maxStacks) return;
    this.cadenceStacks = Math.min(this.cadenceConfig.maxStacks, this.cadenceStacks + 1);
    if (this.cadenceStacks === this.cadenceConfig.maxStacks) {
      this.eventBus.emit('player:cadence-max');
    }
  }

  _updateCadence(dt) {
    if (!this.cadenceConfig || this.cadenceStacks === 0) return;
    if (this._cadenceGrace > 0) {
      this._cadenceGrace -= dt;
      return;
    }
    // Fractional decay accumulated against the integer stack count, so the
    // ramp-down is a smooth bleed rather than a cliff at the grace edge.
    this._cadenceDecayAccum += this.cadenceConfig.decayPerSecond * dt;
    while (this._cadenceDecayAccum >= 1 && this.cadenceStacks > 0) {
      this._cadenceDecayAccum -= 1;
      this.cadenceStacks--;
    }
  }

  _updateArmorPlates(dt) {
    if (this.maxArmorPlates === 0 || this.armorPlates >= this.maxArmorPlates) return;
    this._plateTimer -= dt;
    if (this._plateTimer <= 0) {
      this._plateTimer = this._plateRechargeSeconds;
      this.armorPlates++;
      this.eventBus.emit('player:plate-restored', { plates: this.armorPlates });
    }
  }

  get isDashing() {
    return this._isDashingBurst;
  }

  get dashCharges() {
    return this._dashCharges;
  }

  get dashReady() {
    return this._dashCharges > 0;
  }

  /** 0..1, how charged the next recharging pip is — drives the HUD dash pips (see Game.js's _drawHud). */
  get dashCooldownFraction() {
    if (this._dashCharges >= this.maxDashCharges) return 1;
    const timer = this._rechargeTimers[0];
    return timer === undefined ? 1 : 1 - timer / this.dashCooldown;
  }

  /** True while the ultimate surge window is open — drives the gold player tint. */
  get isSurging() {
    return this._surgeTimer > 0;
  }

  /** Seconds left in the surge window. */
  get surgeRemaining() {
    return this._surgeTimer;
  }

  /** Effective damage multiplier bonus from an active ultimate surge — consulted by CombatSystem. */
  get surgeDamageMultiplier() {
    return this.isSurging ? SURGE_DAMAGE_MULTIPLIER : 1;
  }

  /** Effective weapon fire-rate multiplier from an active ultimate surge — consulted by CombatSystem. */
  get surgeFireRateMultiplier() {
    return this.isSurging ? SURGE_FIRE_RATE_MULTIPLIER : 1;
  }

  /** Slipstream's post-dash move-speed surge (wind legendary pet) — layered into effectiveMoveSpeedMultiplier. */
  get postDashMoveMultiplier() {
    return this._postDashTimer > 0 ? 1 + this.postDashMoveBonus : 1;
  }

  /** Slipstream's post-dash dash-range surge (wind legendary pet) — layered into the dash speed calc. */
  get postDashDashRangeMultiplier() {
    return this._postDashTimer > 0 ? 1 + this.postDashDashRangeBonus : 1;
  }

  /** moveSpeedMultiplier plus Momentum's unhit-time ramp and Adrenaline's low-HP bonus, layered on top rather than mutating the base multiplier. */
  get effectiveMoveSpeedMultiplier() {
    const momentumBonus = this.momentumRampPerSec > 0
      ? Math.min(MOMENTUM_CAP, this.momentumRampPerSec * this._timeSinceDamage) : 0;
    const adrenalineBonus = (this.hasAdrenaline && this.hp / this.maxHp < ADRENALINE_HP_THRESHOLD)
      ? ADRENALINE_SPEED_BONUS : 0;
    return this.moveSpeedMultiplier * this.postDashMoveMultiplier * (1 + momentumBonus + adrenalineBonus);
  }

  /** Adrenaline's damage bonus while below the HP threshold — consulted by CombatSystem alongside surgeDamageMultiplier. */
  get adrenalineDamageMultiplier() {
    return (this.hasAdrenaline && this.hp / this.maxHp < ADRENALINE_HP_THRESHOLD)
      ? ADRENALINE_DAMAGE_MULTIPLIER : 1;
  }

  /**
   * @param {number} dt - fixed simulation timestep (seconds)
   * @param {{x:number,y:number}} moveVec - normalized input vector
   * @param {boolean} dashHeld - true while the dash key is held; a dash starts
   *   automatically whenever a charge is ready
   * @param {boolean} [focusHeld] - true while the focus key is held (§D4):
   *   walk at FOCUS.speedMultiplier and reveal the hull/graze band
   */
  update(dt, moveVec, dashHeld, focusHeld = false) {
    if (moveVec.x !== 0 || moveVec.y !== 0) {
      this.facing = { x: moveVec.x, y: moveVec.y };
    }

    this.isFocused = focusHeld;
    // Eased toward the held state at a fixed rate, so the reveal takes
    // FOCUS.fadeDuration in either direction regardless of framerate.
    const focusStep = dt / FOCUS.fadeDuration;
    this.focusReveal = focusHeld
      ? Math.min(1, this.focusReveal + focusStep)
      : Math.max(0, this.focusReveal - focusStep);

    this._updateDash(dt, moveVec, dashHeld);
    this._surgeTimer = Math.max(0, this._surgeTimer - dt);
    // Graze stacks decay as a group: each new graze refreshes the shared
    // window (see the bullet:grazed listener), and when it lapses the whole
    // stack drops at once rather than bleeding off one at a time.
    if (this._grazeTimer > 0) {
      this._grazeTimer -= dt;
      if (this._grazeTimer <= 0) this.grazeStacks = 0;
    }
    this._updateCadence(dt);
    // Both Act III signatures. Inscription runs after _updateDash so a dash
    // begun this tick already counts as "not still"; both are no-ops for every
    // champion that isn't Quill or Fugue.
    this._updateInscription(dt, moveVec);
    this._updateStance(dt);
    // Tine's flip cooldown — a no-op for everyone else (the timer never leaves 0).
    if (this._attuneFlipTimer > 0) this._attuneFlipTimer -= dt;
    this._updateArmorPlates(dt);
    this.hitFlashTimer = Math.max(0, this.hitFlashTimer - dt);
    this._hitInvulnTimer = Math.max(0, this._hitInvulnTimer - dt);
    // Deathbomb window (§D3). Ticked on SIM dt deliberately: the cue slows
    // the whole world down, and the window slowing with it is the entire
    // reason 0.16s is reactable at all.
    if (this._pendingHit) {
      this._pendingHit.remaining -= dt;
      if (this._pendingHit.remaining <= 0) {
        const { amount, source } = this._pendingHit;
        this._pendingHit = null;
        // The mercy window opened when the hit was caught, not now — passing
        // false keeps the total i-frames a hit grants identical either way.
        this._applyDamage(amount, source, false);
      }
    }
    this._timeSinceDamage += dt;
    // After the clock advances, so the very tick the wait elapses pays out.
    this._updateDebt(dt);
    if (this._postDashTimer > 0) this._postDashTimer -= dt;
    if (this._wellspringCooldown > 0) this._wellspringCooldown -= dt;
    this._flushLifesteal(dt);

    // Flat HP/sec (meta shop) + a fraction of max HP/sec (water pets).
    const hpRegen = this.hpRegenPerSecond + this.maxHp * this.hpRegenPercent;
    if (hpRegen > 0 && this.hp > 0) {
      this.hp = Math.min(this.maxHp, this.hp + hpRegen * dt);
    }

    const speed = this.isDashing
      ? (PLAYER.dashRange * this.dashRangeMultiplier * this.postDashDashRangeMultiplier) / PLAYER.dashDuration
      // Focus scales the walk only — never the dash (see FOCUS in
      // config/constants.js for why).
      : PLAYER.moveSpeed * this.effectiveMoveSpeedMultiplier
        * (this.isFocused ? FOCUS.speedMultiplier : 1);
    const dir = this.isDashing ? this._dashDir : moveVec;
    this.x += dir.x * speed * dt;
    this.y += dir.y * speed * dt;

    this._applyKnockback(dt);

    this._clampToArena();
    // After the clamp, so what's remembered is where the player actually ended
    // up rather than an out-of-bounds intermediate.
    this._recordPath(dt);
  }

  // --- Act III movement history ---------------------------------------------

  _recordPath(dt) {
    for (const entry of this.pathHistory) entry.age += dt;
    // Drop everything past the window from the front (oldest first, so the
    // first surviving entry ends the sweep).
    while (this.pathHistory.length && this.pathHistory[0].age > PATH_HISTORY_SECONDS) {
      this.pathHistory.shift();
    }
    this._pathSampleTimer -= dt;
    if (this._pathSampleTimer > 0) return;
    this._pathSampleTimer = PATH_SAMPLE_INTERVAL;
    this.pathHistory.push({ x: this.x, y: this.y, age: 0 });
  }

  /**
   * Where the player was `seconds` ago, or null if the buffer doesn't reach
   * that far back yet (the first seconds of a fight, or a headless test that
   * never ticks the player). Nearest recorded sample, not interpolated — the
   * sample cadence is already finer than anything reading this cares about.
   */
  positionAgo(seconds) {
    let best = null;
    let bestDelta = Infinity;
    for (const entry of this.pathHistory) {
      const delta = Math.abs(entry.age - seconds);
      if (delta < bestDelta) {
        bestDelta = delta;
        best = entry;
      }
    }
    // Refuse to answer with the buffer's own oldest sample when the request is
    // well past its reach — a hazard pinned to a stale point would sit still
    // and read as a bug rather than as a replay.
    if (!best || bestDelta > PATH_SAMPLE_INTERVAL * 3) return null;
    return { x: best.x, y: best.y };
  }

  /** The recorded path between `fromAgo` and `toAgo` seconds back, oldest first. */
  pathBetween(fromAgo, toAgo) {
    const older = Math.max(fromAgo, toAgo);
    const newer = Math.min(fromAgo, toAgo);
    return this.pathHistory
      .filter((entry) => entry.age <= older && entry.age >= newer)
      .map(({ x, y }) => ({ x, y }));
  }

  /** Phasewalker pact: raises the charge cap and grants the new charge immediately (no cooldown wait). */
  addDashCharge() {
    this.maxDashCharges += 1;
    this._dashCharges += 1;
  }

  /** One-shot recoil impulse (px/sec) in the direction of (dx, dy); decays in _applyKnockback. */
  applyKnockback(dx, dy, magnitude) {
    const len = Math.hypot(dx, dy) || 1;
    this._knockback.x = (dx / len) * magnitude;
    this._knockback.y = (dy / len) * magnitude;
  }

  _applyKnockback(dt) {
    if (this._knockback.x === 0 && this._knockback.y === 0) return;
    this.x += this._knockback.x * dt;
    this.y += this._knockback.y * dt;
    // Exponential-ish decay: ~85% shed per tick so the bounce is a short, snappy
    // shove rather than a long slide, and self-zeroes quickly.
    const decay = Math.max(0, 1 - 10 * dt);
    this._knockback.x *= decay;
    this._knockback.y *= decay;
    if (Math.abs(this._knockback.x) < 1 && Math.abs(this._knockback.y) < 1) {
      this._knockback.x = 0;
      this._knockback.y = 0;
    }
  }

  _updateDash(dt, moveVec, dashHeld) {
    for (let i = this._rechargeTimers.length - 1; i >= 0; i--) {
      this._rechargeTimers[i] -= dt;
      if (this._rechargeTimers[i] <= 0) {
        this._rechargeTimers.splice(i, 1);
        this._dashCharges = Math.min(this.maxDashCharges, this._dashCharges + 1);
        // Purely a feedback hook (pip blink + soft chime) — a charge coming
        // back is the "you can go again" moment in a dodge-driven game.
        this.eventBus.emit('player:dash-refilled', { charges: this._dashCharges });
      }
    }

    if (this._isDashingBurst) {
      this._dashTimer -= dt;
      // i-frames last dashInvincibleDuration, which covers (>=) the dash
      // burst itself so you can't get tagged the instant it ends.
      if (PLAYER.dashDuration - this._dashTimer >= PLAYER.dashInvincibleDuration) {
        this.isInvincible = false;
      }
      if (this._dashTimer <= 0) {
        this._isDashingBurst = false;
        this.isInvincible = false;
        this.eventBus.emit('player:dash-end', { x: this.x, y: this.y });
      }
      return;
    }

    // freeDash (Kestrel's Skybreaker ultimate) makes dashing cost nothing:
    // usable at zero charges, and spending one neither decrements the pool
    // nor queues a recharge — the pips just sit full for the window.
    if (dashHeld && (this._dashCharges > 0 || this.freeDash)) {
      // Dash toward current movement input, or last faced direction if
      // standing still — dashing in place still has a use (i-frames).
      const hasInput = moveVec.x !== 0 || moveVec.y !== 0;
      this._dashDir = hasInput ? { ...moveVec } : { ...this.facing };
      this._isDashingBurst = true;
      this._dashTimer = PLAYER.dashDuration;
      this.isInvincible = true;
      if (!this.freeDash) {
        this._dashCharges--;
        this._rechargeTimers.push(this.dashCooldown);
      }
      if (this.postDashMoveBonus > 0 || this.postDashDashRangeBonus > 0) this._postDashTimer = POST_DASH_DURATION;
      // Flip attunement on the dash (Antiphon polarity — inert elsewhere).
      this.attunement = this.attunement === 'warm' ? 'cold' : 'warm';
      this.eventBus.emit('player:dash-start', { x: this.x, y: this.y, attunement: this.attunement });
    }
  }

  // Both trickle-heal sources (lifesteal and Cairn's Debt repayment) bank
  // their amounts and flush on one shared cadence — each heals a little every
  // tick, and a "+1" pop-up per tick would be a wall of noise.
  _flushLifesteal(dt) {
    if (this._lifestealAccum <= 0 && this._debtHealAccum <= 0) return;
    this._lifestealFlushTimer -= dt;
    if (this._lifestealFlushTimer > 0) return;
    this._lifestealFlushTimer = LIFESTEAL_FLUSH_INTERVAL;
    if (this._lifestealAccum > 0) {
      this.eventBus.emit('player:healed', { amount: this._lifestealAccum, source: 'lifesteal' });
      this._lifestealAccum = 0;
    }
    if (this._debtHealAccum > 0) {
      this.eventBus.emit('player:healed', { amount: this._debtHealAccum, source: 'debt' });
      this._debtHealAccum = 0;
    }
  }

  _clampToArena() {
    this.x = Math.min(ARENA.x + ARENA.width - this.radius, Math.max(ARENA.x + this.radius, this.x));
    this.y = Math.min(ARENA.y + ARENA.height - this.radius, Math.max(ARENA.y + this.radius, this.y));
  }

  /** True while the post-hit mercy window is still running. */
  get isHitInvulnerable() {
    return this._hitInvulnTimer > 0;
  }

  /** Seconds left in the post-hit mercy window — drives its blink tell. */
  get hitInvulnRemaining() {
    return this._hitInvulnTimer;
  }

  /** True while a hit is caught mid-air and can still be deathbombed (§D3). */
  get hasPendingHit() {
    return this._pendingHit !== null;
  }

  /**
   * Voids a caught hit — the deathbomb itself. Called by UltimateSystem on
   * activation; returns whether there was anything to refund, so the caller
   * can tell a bomb from an ordinary cast. The damage is discarded whole: no
   * HP comes off, nothing banks into Cairn's Debt, and no player:damaged
   * fires, so every listener downstream (Reckless Pact's drain, Reprisal,
   * the death recap) correctly never sees a hit that didn't happen.
   */
  voidPendingHit() {
    if (!this._pendingHit) return false;
    const { amount, source } = this._pendingHit;
    this._pendingHit = null;
    this.eventBus.emit('player:deathbombed', { amount, source, x: this.x, y: this.y });
    return true;
  }

  /**
   * Drops a caught hit without crediting it as a bomb — for the run ending
   * (or otherwise being torn down) while one is still in the air, where
   * resolving it later would land damage after the fight it belonged to.
   */
  clearPendingHit() {
    this._pendingHit = null;
  }

  /**
   * Applies damage unless currently i-framed or dodged; returns true if it landed.
   *
   * `source` names what dealt it — a boss pattern's name, a minion type id, or
   * a hazard id — and is carried on player:damaged for the death recap
   * (systems/DamageLedger.js). It has no effect on the damage itself; every
   * caller that can't say what hit the player may leave it null.
   */
  takeDamage(amount, source = null) {
    if (this.isInvincible || this._hitInvulnTimer > 0) return false;
    const effectiveDodgeChance = this.dodgeChance + (this.isSurging ? SURGE_DODGE_BONUS : 0);
    if (effectiveDodgeChance > 0 && Math.random() < effectiveDodgeChance) {
      this.eventBus.emit('player:dodged');
      return false;
    }
    // Rook's Aegis Plating: a plate eats the hit whole. Checked after the
    // dodge roll (a free avoid shouldn't burn a consumable) but before any
    // reduction math, since the plate's whole point is that the number never
    // reaches HP at all. Opens the mercy window on the way out like a real
    // hit does — otherwise a crowded tick would strip all three plates at
    // once, which is exactly the same-tick pile-on that window exists for.
    if (this.armorPlates > 0) {
      this.armorPlates--;
      this._plateTimer = this._plateRechargeSeconds;
      this.hitFlashTimer = HIT_FLASH_DURATION;
      this._hitInvulnTimer = PLAYER.hitInvulnDuration;
      this.eventBus.emit('player:plate-broken', { plates: this.armorPlates });
      return false;
    }
    // Deathbomb (§D3). Everything above this line is a *free* avoid — a
    // dodge roll or a plate — and must stay inline: making the player spend
    // a full ultimate meter on a hit that was never going to land is the one
    // way this mechanic could feel worse than not having it. From here the
    // hit is real, so it's the right place to catch it.
    //
    // Only caught when there's a full bar to spend, so a run that can't bomb
    // takes damage on exactly the old timing. No re-entrancy guard is needed:
    // the mercy window opens below and is four times the catch window, so a
    // second hit can't arrive while one is held.
    if (this.deathbombWindow > 0 && this.canDeathbomb?.()) {
      this._pendingHit = { amount, source, remaining: this.deathbombWindow };
      // Deliberately no hitFlash here — the red flinch means "that hurt",
      // and this hit hasn't hurt yet. The gold window cue (JuiceDirector)
      // carries the moment; the flinch fires with the damage if it lapses.
      this._hitInvulnTimer = PLAYER.hitInvulnDuration;
      this.eventBus.emit('player:deathbomb-window', {
        window: this.deathbombWindow, x: this.x, y: this.y,
      });
      // True: the hit landed. Callers use this to consume the bullet, and a
      // caught hit must still eat the bullet that caused it.
      return true;
    }
    return this._applyDamage(amount, source, true);
  }

  /**
   * The damage half of takeDamage, split out so the deathbomb can defer it
   * (see the pending-hit tick in update()). Runs after every free-avoid check
   * has already passed, so it always lands. `openMercyWindow` is false on the
   * deferred path, where the window was already opened at catch time.
   */
  _applyDamage(amount, source, openMercyWindow) {
    // Percent damage reduction: Stoneskin (earth legendary) and the Bulwark
    // passive both feed this.damageReduction, capped at 0.9 so a hit always
    // chips through.
    // Fugue's stance rides the same percent-reduction bracket as Stoneskin and
    // Bulwark — and can be NEGATIVE (Statement's exposure), which is why the
    // clamp below is two-sided rather than only capping the top.
    const percentReduction = Math.min(0.9, Math.max(-1, this.damageReduction + this.stanceDamageReduction));
    const applied = amount
      * (1 - percentReduction)
      * (1 - this.ultimateDamageReduction)
      * this.incomingDamageMultiplier;
    this.hp = Math.max(0, this.hp - applied);
    // Cairn's Debt: bank a share of what was actually taken (not the raw
    // number — mitigated damage was never lost, so it isn't owed back).
    if (this.debtConfig) {
      this.debtBank = Math.min(this.maxHp, this.debtBank + applied * this.debtConfig.bankFraction);
    }
    // Wellspring (water legendary): a clutch heal the instant a hit leaves the
    // player below half HP, then on cooldown so it's a lifeline, not sustain.
    // Guarded by hp > 0 so it never revives from a lethal blow (that would
    // tread on the extra-life Shard perk).
    if (this.wellspringHealPct > 0 && this._wellspringCooldown <= 0
        && this.hp > 0 && this.hp < this.maxHp * 0.5) {
      const before = this.hp;
      this.hp = Math.min(this.maxHp, this.hp + this.maxHp * this.wellspringHealPct);
      this._wellspringCooldown = WELLSPRING_COOLDOWN;
      this.eventBus.emit('player:healed', { amount: this.hp - before, source: 'wellspring' });
    }
    this.hitFlashTimer = HIT_FLASH_DURATION;
    // Opens only on a hit that actually landed — dodges and i-framed hits
    // bail above, so neither extends the window.
    if (openMercyWindow) this._hitInvulnTimer = PLAYER.hitInvulnDuration;
    // `applied` is the number that actually came off HP (post-reduction,
    // post-multiplier), so the recap tallies what the player really lost
    // rather than the raw value the source nominally deals.
    this.eventBus.emit('player:damaged', {
      hp: this.hp, maxHp: this.maxHp, amount: applied, source,
    });
    if (this.hp === 0) this.eventBus.emit('player:died', { source });
    return true;
  }
}
